import { CompactText } from 'ui/CompactText';
import { useRef, useState, type FC } from 'react';
import { PageHead } from 'ui/kit/PageHead';
import * as yup from 'yup';

import { addNotification } from 'notifications';
import { Button, Select, TextField } from 'ui';
import { FadeIn } from 'ui/kit/uiMotion';

import { getRole } from 'api/tokenStorage';
import { useVerificationEdits } from 'hooks/useVerificationEdits';
import { ProfileChangeList, type ProfileChangeRow } from 'ui/ProfileChangeList';
import { UnsavedLeaveGuard } from 'ui/kit/UnsavedLeaveGuard';
import { SEX_SELECT_OPTIONS } from 'utils/sexLabels';
import { getApiErrorMessage } from 'utils/getApiErrorMessage';
import { useGetCurrentProfile, useUpdateProfile } from '../model/services';

import { FormContainer } from 'components/formsV2/formContainer/index';
import { ProfileContactsSection } from './ProfileContactsSection';
import classes from './StudentProfileEdit.module.scss';
import { studentStatusChip } from './studentProfileScreen';

const SELECT_OPTION_PROPS_MAP = {
  key: 'value' as const,
  title: 'label' as const,
  viewValue: 'label' as const,
};

const REGISTRANT_TYPE_OPTIONS = [
  { value: 'self', label: 'Я занимаюсь сам(а)' },
  { value: 'parent', label: 'Я родитель' },
];

const SEX_OPTIONS = SEX_SELECT_OPTIONS.map(({ value, label }) => ({ value, label }));

interface ProfileFormValues {
  name: string;
  surname: string;
  patronymic: string;
  sex: string;
  date_of_birth: string;
  registrant_type: 'self' | 'parent';
}

const baseSchema = yup.object({
  name: yup.string().trim().required('Укажи имя'),
  registrant_type: yup.string(),
});

const REGISTRANT_LABEL: Record<string, string> = {
  self: 'Я занимаюсь сам(а)',
  parent: 'Я родитель',
};

function studentChangeRows(
  values: ProfileFormValues,
  profile?: {
    name?: string | null;
    surname?: string | null;
    patronymic?: string | null;
    sex?: number | string | null;
    date_of_birth?: string | null;
    registrant_type?: string | null;
  },
): ProfileChangeRow[] {
  const rows: ProfileChangeRow[] = [];
  const push = (field: string, label: string, before: string, after: string) => {
    if (before === after) return;
    rows.push({ field, label, change_type: 'modified', before: before || ' - ', after: after || ' - ' });
  };
  push('surname', 'Фамилия', profile?.surname || '', values.surname.trim());
  push('name', 'Имя', profile?.name || '', values.name.trim());
  push('patronymic', 'Отчество', profile?.patronymic || '', values.patronymic.trim());
  const sexLabel = (value: string) =>
    SEX_OPTIONS.find((item) => item.value === value)?.label || ' - ';
  push(
    'sex',
    'Пол',
    sexLabel(profile?.sex != null ? String(profile.sex) : ''),
    sexLabel(values.sex),
  );
  push(
    'date_of_birth',
    'Дата рождения',
    (profile?.date_of_birth || '').slice(0, 10),
    values.date_of_birth,
  );
  push(
    'registrant_type',
    'Кто регистрируется',
    REGISTRANT_LABEL[profile?.registrant_type || 'self'] || '',
    REGISTRANT_LABEL[values.registrant_type] || values.registrant_type,
  );
  return rows;
}

type Props = {
  onCancel: () => void;
};

export const StudentProfileEdit: FC<Props> = ({ onCancel }) => {
  const { data: currentProfile } = useGetCurrentProfile();
  const updateProfile = useUpdateProfile();
  const verification = useVerificationEdits();
  const [closeAsk, setCloseAsk] = useState(false);
  const saveOkRef = useRef(false);
  const isStudent = getRole() === 'student';
  const status = currentProfile?.verification_status || '';
  const needsFix = status === 'changes_requested' || status === 'rejected';
  const chip = studentStatusChip(status);

  const initialValues: ProfileFormValues = {
    name: currentProfile?.name || '',
    surname: currentProfile?.surname || '',
    patronymic: currentProfile?.patronymic || '',
    sex: currentProfile?.sex != null ? String(currentProfile.sex) : '',
    date_of_birth: currentProfile?.date_of_birth || '',
    registrant_type: (currentProfile?.registrant_type as 'self' | 'parent') || 'self',
  };

  const handleSubmit = async (values: ProfileFormValues) => {
    const formData = new FormData();

    if (!(currentProfile?.name || '').trim() && values.name.trim()) {
      formData.append('name', values.name.trim());
    }
    if (!(currentProfile?.surname || '').trim() && values.surname.trim()) {
      formData.append('surname', values.surname.trim());
    }
    if (!(currentProfile?.patronymic || '').trim() && values.patronymic.trim()) {
      formData.append('patronymic', values.patronymic.trim());
    }
    if (currentProfile?.sex == null && values.sex !== '') formData.append('sex', values.sex);
    if (!(currentProfile?.date_of_birth || '') && values.date_of_birth) {
      formData.append('date_of_birth', values.date_of_birth);
    }
    if (isStudent) {
      formData.append('registrant_type', values.registrant_type);
    }
    saveOkRef.current = false;
    try {
      await updateProfile.mutateAsync(formData);
      saveOkRef.current = true;
      addNotification('Правки отправлены на проверку.', 'success');
    } catch (error) {
      addNotification(
        getApiErrorMessage(
          error,
          'Произошла ошибка, попробуйте позже или обратитесь к администратору',
        ),
        'error',
      );
    }
  };

  return (
    <FadeIn>
      <main className={classes.page}>
        <PageHead crumb={<>Личный кабинет / Мой профиль</>} title={<>{needsFix ? 'Исправление данных' : 'Редактирование профиля'}</>} />

        <section className={`${classes.card} ${needsFix ? classes.cardFix : ''}`}>
          <h2 className={classes.cardTitle}>Личные данные</h2>
          <p className={classes.body}>
            Имя, фамилия, отчество, пол и дата рождения не меняются. Пустое поле можно заполнить один раз.
          </p>
          {needsFix && chip ? (
            <span className={classes.chip}>
              <span className={classes.chipDot} aria-hidden />
              {chip}
            </span>
          ) : null}
          {needsFix ? (
            <p className={classes.body}>Уточни имя. Контакты здесь не отображаются.</p>
          ) : null}

          <FormContainer<ProfileFormValues>
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={baseSchema}
            validateOnChange={true}
            validateOnBlur={true}
            enableReinitialize
          >
            {({ values, setFieldValue, dirty, resetForm, validateForm, submitForm, setTouched }) => {
              const localChanges = studentChangeRows(values, currentProfile);
              return (
              <div className={classes.form}>
                <UnsavedLeaveGuard
                  when={dirty}
                  audience="student"
                  localOpen={closeAsk}
                  onLocalStay={() => setCloseAsk(false)}
                  onLocalDiscard={() => {
                    resetForm();
                    onCancel();
                  }}
                  onSave={async () => {
                    const errors = await validateForm();
                    if (errors && Object.keys(errors).length) {
                      const message = Object.values(errors).find((item) => typeof item === 'string');
                      setTouched(
                        Object.fromEntries(Object.keys(errors).map((key) => [key, true])),
                        false,
                      );
                      addNotification(
                        typeof message === 'string' ? message : 'Проверь поля и сохрани еще раз.',
                        'error',
                      );
                      return false;
                    }
                    await submitForm();
                    return saveOkRef.current;
                  }}
                />
                <div className={`${classes.row} ${classes.row3}`}>
                  <div className={classes.cell}>
                    <TextField
                      shape="brick"
                      type="text"
                      placeholder="Фамилия"
                      label="Фамилия"
                      size="lg"
                      value={values.surname}
                      disabled={Boolean((currentProfile?.surname || '').trim())}
                      onChange={(val) => setFieldValue('surname', val)}
                    />
                  </div>
                  <div className={classes.cell}>
                    <TextField
                      shape="brick"
                      type="text"
                      placeholder="Имя"
                      label="Имя"
                      size="lg"
                      value={values.name}
                      disabled={Boolean((currentProfile?.name || '').trim())}
                      onChange={(val) => setFieldValue('name', val)}
                    />
                  </div>
                  <div className={classes.cell}>
                    <TextField
                      shape="brick"
                      type="text"
                      placeholder="Отчество"
                      label="Отчество"
                      size="lg"
                      value={values.patronymic}
                      disabled={Boolean((currentProfile?.patronymic || '').trim())}
                      onChange={(val) => setFieldValue('patronymic', val)}
                    />
                  </div>
                </div>
                <div className={classes.row}>
                  <div className={classes.cell}>
                    <Select
                      shape="brick"
                      size="lg"
                      variant="outlined"
                      widthFullSize
                      label="Пол"
                      placeholder="Не указан"
                      options={SEX_OPTIONS}
                      optionPropsMap={SELECT_OPTION_PROPS_MAP}
                      value={values.sex || null}
                      disabled={currentProfile?.sex != null}
                      onChange={(value) => setFieldValue('sex', value ? String(value) : '')}
                      createPortal
                    />
                  </div>
                  <div className={classes.cell}>
                    <TextField
                      shape="brick"
                      type="date"
                      placeholder="ДД.ММ.ГГГГ"
                      label="Дата рождения"
                      size="lg"
                      value={values.date_of_birth}
                      disabled={Boolean(currentProfile?.date_of_birth)}
                      onChange={(val) => setFieldValue('date_of_birth', val)}
                    />
                  </div>
                  {isStudent ? (
                  <div className={classes.cell}>
                    <Select
                      shape="brick"
                      size="lg"
                      variant="outlined"
                      widthFullSize
                      label="Кто регистрируется"
                      placeholder="Выберите"
                      options={REGISTRANT_TYPE_OPTIONS}
                      optionPropsMap={SELECT_OPTION_PROPS_MAP}
                      value={values.registrant_type}
                      onChange={(value) => {
                        const next = (value as 'self' | 'parent') || 'self';
                        setFieldValue('registrant_type', next);
                      }}
                      createPortal
                    />
                  </div>
                ) : null}
                </div>

                <CompactText className={classes.hint} tone={'info'}>
                  После сохранения правки уйдут на проверку. Телефон и почту по-прежнему меняют в настройках.
                </CompactText>
                <h2 className={classes.cardTitle}>Проверка</h2>
                <ProfileChangeList
                  title="Что изменится"
                  rows={localChanges}
                  empty={
                    !isStudent && verification.draftChanges.length
                      ? undefined
                      : 'Пока нет новых правок. Измени поле выше - здесь появится сравнение.'
                  }
                />
                {!isStudent && verification.draftChanges.length ? (
                  <ProfileChangeList title="Уже на проверке" rows={verification.draftChanges} />
                ) : null}
                {needsFix ? (
                  <p className={classes.foot}>
                    После сохранения профиль снова поступит на проверку.
                  </p>
                ) : null}

                <div className={classes.actions}>
                  <Button
                    className={`${classes.primary} ${needsFix ? classes.fixPrimary : ''}`}
                    variant="filled"
                    size="md"
                    color="primary"
                    shape="brick"
                    type="button"
                    loading={updateProfile.isPending}
                    onClick={async (event) => {
                      event.preventDefault();
                      if (updateProfile.isPending) return;
                      if (!dirty) {
                        addNotification('Пока нет новых правок. Измени поле и отправь еще раз.', 'info');
                        return;
                      }
                      const formErrors = await validateForm();
                      if (formErrors && Object.keys(formErrors).length > 0) {
                        const text = Object.values(formErrors).find((item) => typeof item === 'string');
                        addNotification(
                          typeof text === 'string' ? text : 'Проверь поля и отправь еще раз.',
                          'error',
                        );
                        return;
                      }
                      await submitForm();
                    }}
                  >
                    Отправить на проверку
                  </Button>
                  <button
                    type="button"
                    className={`${classes.secondary} ${needsFix ? classes.fixSecondary : ''}`}
                    onClick={() => {
                      if (dirty) {
                        setCloseAsk(true);
                        return;
                      }
                      onCancel();
                    }}
                  >
                    Отмена
                  </button>
                </div>
              </div>
              );
            }}
          </FormContainer>

        </section>
        {needsFix ? null : (
          <section className={`${classes.card} ${classes.contacts}`}>
            <ProfileContactsSection />
          </section>
        )}
      </main>
    </FadeIn>
  );
};
